import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useEffectOnce } from './useEffectOnce';

enum ChevronDirection {
  LEFT = 'left',
  RIGHT = 'right',
}

// Tollerance when to show / hide chevron.
// If the bar is scrolled e.g. 5px we are not showing
// the chevron yet
const SHOW_CHEVRON_OFFSET = 8;
const CHEVRON_WIDTH = 24;

interface Args extends IntersectionObserverInit {
  freezeOnceVisible?: boolean;
}

const useIntersectionObserver = (
  elementRef: RefObject<Element | null>,
  {
    threshold = 0,
    root = null,
    rootMargin = '0%',
    freezeOnceVisible = false,
  }: Args = {}
): IntersectionObserverEntry | undefined => {
  const [entry, setEntry] = useState<IntersectionObserverEntry>();

  const frozen = entry?.isIntersecting && freezeOnceVisible;

  const updateEntry = ([entry]: IntersectionObserverEntry[]): void => {
    setEntry(entry);
  };

  useEffect(() => {
    const node = elementRef?.current; // DOM Ref
    const hasIOSupport = !!window.IntersectionObserver;

    if (!hasIOSupport || frozen || !node) return;

    const observerParams = { threshold, root, rootMargin };
    const observer = new IntersectionObserver(updateEntry, observerParams);

    observer.observe(node);

    return () => observer.disconnect();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, JSON.stringify(threshold), root, rootMargin, frozen]);

  return entry;
};

const getScrollTarget = (
  wrapperEl: HTMLElement,
  direction: ChevronDirection
) => {
  const wrapperElEdgePosition = wrapperEl.getBoundingClientRect()[direction];
  const children = wrapperEl.children;
  // Find the relevant child index
  const scrollTargetIndex = Array.from(children).findIndex(child => {
    const childEdgePosition = child.getBoundingClientRect()[direction];
    return (
      childEdgePosition >
      wrapperElEdgePosition +
        (direction === ChevronDirection.LEFT ? CHEVRON_WIDTH : -CHEVRON_WIDTH)
    );
  });
  const child = children[scrollTargetIndex > 0 ? scrollTargetIndex - 1 : 0];
  return child instanceof HTMLElement ? child : null;
};

const scrollWrapperToPosition = (wrapperEl: HTMLElement, position: number) =>
  wrapperEl.scrollTo({ left: position, behavior: 'smooth' });
const isScrollPossible = (wrapperEl: any): wrapperEl is HTMLElement =>
  Boolean(wrapperEl) && wrapperEl instanceof HTMLElement;
const getScrollLeftPosition = (
  scrollTarget: HTMLElement,
  wrapperWidth: number
) =>
  scrollTarget.offsetLeft -
  (wrapperWidth - scrollTarget.offsetWidth - 2 * CHEVRON_WIDTH);
const getScrollRightPosition = (scrollTarget: HTMLElement) =>
  scrollTarget.offsetLeft + CHEVRON_WIDTH;

const onChevronClick = (
  wrapperEl: HTMLElement,
  wrapperWidth: number,
  direction: ChevronDirection
) => {
  const scrollTarget = getScrollTarget(wrapperEl, direction);
  if (scrollTarget) {
    scrollWrapperToPosition(
      wrapperEl,
      direction === ChevronDirection.LEFT
        ? getScrollLeftPosition(scrollTarget, wrapperWidth)
        : getScrollRightPosition(scrollTarget)
    );
  }
};
export const useChevronSliders = (
  wrapperWidth: number,
  children?: ReactNode
) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(wrapperRef);
  const isVisible = !!entry?.isIntersecting;
  const [scrollLeft, setScrollLeft] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);

  const isLeftChevronVisible = useMemo(
    () => scrollLeft > SHOW_CHEVRON_OFFSET,
    [scrollLeft]
  );
  const isRightChevronVisible = useMemo(
    () => scrollLeft + wrapperWidth < contentWidth - SHOW_CHEVRON_OFFSET,
    [scrollLeft, wrapperWidth, contentWidth]
  );
  const wrapperOnScroll = useCallback(() => {
    const wrapperScrollLeft = wrapperRef.current?.scrollLeft;
    if (wrapperScrollLeft === undefined) return;
    setScrollLeft(wrapperScrollLeft);
  }, [wrapperRef]);

  const getOnChevronClick = useCallback(
    (direction: ChevronDirection) => () => {
      if (isScrollPossible(wrapperRef.current)) {
        onChevronClick(wrapperRef.current, wrapperWidth, direction);
      }
    },
    [wrapperRef, wrapperWidth]
  );

  useEffectOnce(() => {
    if (!wrapperRef.current) return;
    setContentWidth(wrapperRef.current.scrollWidth);
  });
  useEffect(() => {
    if (isVisible) {
      if (!wrapperRef.current) return;
      setContentWidth(wrapperRef.current.scrollWidth);
    }
  }, [isVisible, children]);

  return {
    wrapperRef,
    isLeftChevronVisible,
    isRightChevronVisible,
    onLeftChevronClick: getOnChevronClick(ChevronDirection.LEFT),
    onRightChevronClick: getOnChevronClick(ChevronDirection.RIGHT),
    wrapperOnScroll,
  };
};
